import styled from '@emotion/styled';
import BaseImage from '@base/Image';
import { Fonts, mediaQuery, Spacings } from '@styles/index';

import Typography from 'aquilla/core/atoms/Typography';
import BackGroundImage from './BackgroundImage';

// Journey Block Images
const youTubeIcon = 'about/youtube.png';
const postCardIcon = 'about/postcard.png';
const liveIcon = 'about/live.png';
const rocketIcon = 'about/rocket.svg';
const journeyIcon = 'about/journey.svg';

const Container = styled.div`
  margin-top: 144px;
  grid-column: span 24;
  position: relative;
  ${mediaQuery.uptoTablet} {
    margin-top: 102px;
  }
`;

const MissionGroup = styled(Typography)`
  grid-column: span 11;
  margin-bottom: ${Spacings.SPACING_20B};
  margin-top: ${Spacings.SPACING_16B};
  ${mediaQuery.uptoTablet} {
    margin-bottom: ${Spacings.SPACING_9B};
    grid-column: span 24;
    width: 100%;
    margin-top: ${Spacings.SPACING_8B};
  }
`;

const Mission = styled(Typography)`
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.LARGE};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  letter-spacing: 2px;
  color: var(--color-i-green);
  ${mediaQuery.uptoTablet} {
    display: block;
    font-size: ${Fonts.ULTRA_SMALL};
    text-align: center;
  }
`;

const Description = styled(Typography)`
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.LARGE_HEADING};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: var(--color-i-black);
  margin-top: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H3};
    text-align: center;
  }
`;

const Journey = styled.div`
  grid-column: span 24;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-row-gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    display: grid;
    grid-template-columns: repeat(24, 1fr);
    grid-column-gap: ${Spacings.SPACING_2B};
    grid-row-gap: ${Spacings.SPACING_2B};
  }
`;

const HistoryHolder = styled.div`
  grid-column: span 13;
  margin-top: 112px;
  ${mediaQuery.uptoTablet} {
    margin-top: 0px;
    grid-column: span 24;
    width: 100%;
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;

const History = styled.div`
  padding-bottom: ${Spacings.SPACING_15B};
  position: relative;
  ${mediaQuery.uptoTablet} {
    margin-bottom: ${Spacings.SPACING_14B};
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const Year = styled(Typography)`
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.H2};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: var(--color-i-black);
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_5B};
    font-size: ${Fonts.H3};
    text-align: center;
  }
`;

const SubText = styled(Typography)`
  font-size: ${Fonts.H3};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  color: var(--color-text-secondary);
  padding-left: ${Spacings.SPACING_19B};
  ${mediaQuery.uptoTablet} {
    padding-left: ${Spacings.SPACING_0B};
    margin-top: ${Spacings.SPACING_3B};
    font-size: ${Fonts.SMALL};
    text-align: center;
  }
`;

const JourneyImageContainer = styled.div`
  width: ${Spacings.SPACING_20B};
  padding-right: ${Spacings.SPACING_7B};
  ${mediaQuery.uptoTablet} {
    padding-left: ${Spacings.SPACING_15};
    padding-right: ${Spacings.SPACING_0B};

    img {
      width: ${Spacings.SPACING_50};
      height: auto;
    }
  }
`;

const Image = styled(BaseImage)`
  width: 100%;
  z-index: 1;
  object-fit: contain;
  ${mediaQuery.uptoTablet} {
    padding-right: ${Spacings.SPACING_0B};
  }
`;

const ImgContainer = styled.div`
  display: flex;
  align-items: center;
  ${mediaQuery.uptoTablet} {
    flex-direction: column;
    align-items: center;
  }
`;

const Line = styled.div`
  border-left: ${Spacings.SPACING_1B} dotted var(--color-divider);
  height: 99%;
  position: absolute;
  left: 25px;
  display: ${({ $display }) => ($display === 'lg' ? 'block' : 'none')};
  opacity: ${({ $opacity }) => $opacity};
  z-index: -1;
  ${mediaQuery.uptoTablet} {
    left: 50%;
    justify-content: center;
    align-items: center;
    border-width: 2px;
    display: ${({ $display }) => ($display === 'sm' ? 'flex' : 'none')};
  }
`;

const historys = [
  {
    image: youTubeIcon,
    year: '2015',
    subText:
      'From a YouTube channel that started in 2010, we became a feature-rich educational technology platform in 2015'
  },
  {
    image: postCardIcon,
    year: '2017',
    subText:
      'Became India’s largest online learning platform with 1m+ learners & 40k live classes'
  },
  {
    image: liveIcon,
    year: '2018',
    subText:
      'Became the biggest Live Learning platform with 200k+ daily active users who are learning with live classes'
  },
  {
    image: rocketIcon,
    year: '2020',
    subText:
      'We acquired Kreatryx, PrepLadder and Codechef to encompass more avenues of learning, we also launched our own Graphy app for enjoying interactive stories & books'
  },
  {
    image: journeyIcon,
    year: '2021',
    subText:
      'Revamped our entire learning experience on the app and launched it to all the learners via our first ever Unveil 1.0 event'
  }
];

const JourneyBlock = () => {
  return (
    <Container>
      <BackGroundImage />
      <Journey>
        <MissionGroup as="div">
          <Mission>FROM HUMBLE BEGINNINGS</Mission>
          <Description variant="h2">Our journey so far</Description>
        </MissionGroup>
        <HistoryHolder>
          {historys.map((history, index) => (
            <History key={history.year}>
              <ImgContainer>
                {historys.length - 1 !== index && (
                  <Line $display="sm" $opacity={(4 - index) * 0.25} />
                )}
                <JourneyImageContainer>
                  <Image
                    src={history.image}
                    alt="our journey"
                    width={50}
                    height={50}
                  />
                </JourneyImageContainer>
                <Year>{history.year}</Year>
              </ImgContainer>
              {historys.length - 1 !== index && (
                <Line $display="lg" $opacity={(4 - index) * 0.25} />
              )}
              <SubText>{history.subText}</SubText>
            </History>
          ))}
        </HistoryHolder>
      </Journey>
    </Container>
  );
};

export default JourneyBlock;
